// client/src/pages/TestimonialFormPage.js — the testimonial form behind the link in a
// review request. Public: the token in the URL is the only authority, and it grants one
// thing — sending one testimonial about one closing.
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

const TestimonialFormPage = () => {
    const { token } = useParams();
    const [info, setInfo] = useState(null);
    const [error, setError] = useState('');
    const [done, setDone] = useState('');
    const [f, setF] = useState({ author_name: '', body: '', rating: '', name_style: 'first_initial', consent_publish: false, website: '' });
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        fetch(`/api/reviews/public/t/${encodeURIComponent(token)}`)
            .then(async (r) => { const j = await r.json(); if (!r.ok) throw new Error(j.message); setInfo(j); })
            .catch((e) => setError(e.message || 'This link is not valid.'));
    }, [token]);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true); setError('');
        try {
            const r = await fetch(`/api/reviews/public/t/${encodeURIComponent(token)}`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...f, rating: f.rating ? Number(f.rating) : null }),
            });
            const j = await r.json();
            if (!r.ok) throw new Error(j.message);
            setDone(j.message);
        } catch (err) { setError(err.message); } finally { setBusy(false); }
    };
    const shownName = info?.name_styles?.[f.name_style] || '';

    return (
        <div className="min-h-screen bg-slate-100 flex items-start justify-center p-4">
            <div className="bg-white rounded-xl shadow max-w-xl w-full p-6 space-y-4">
                <h1 className="text-xl font-bold text-slate-800">{info?.brokerage || 'Business Real Estate Corp.'}</h1>
                {error && <p className="text-red-700 text-sm">{error}</p>}
                {done && <p className="text-green-700">{done}</p>}
                {info && !done && (info.already_submitted
                    ? <p>We already have your testimonial — thank you. To change or remove it, contact {info.agent_name || 'your agent'}.</p>
                    : (
                        <form onSubmit={submit} className="space-y-3">
                            <p className="text-slate-700">Hi {info.first_name}, thank you for working with {info.agent_name || 'us'}. Tell us how it went — good or bad.</p>
                            {info.links?.length > 0 && <p className="text-sm text-slate-600">Prefer a public review site? {info.links.map((l, i) => <span key={l.platform}>{i ? ' · ' : ''}<a className="text-indigo-700 underline" href={l.url} target="_blank" rel="noopener noreferrer">{l.label}</a></span>)}</p>}
                            <label className="block text-sm">Your name<input required className="w-full p-2 border rounded" value={f.author_name} onChange={(e) => setF({ ...f, author_name: e.target.value })} /></label>
                            <label className="block text-sm">Your testimonial<textarea required minLength={10} maxLength={4000} className="w-full p-2 border rounded h-36" value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} /></label>
                            <label className="block text-sm">Rating (optional)
                                <select className="w-full p-2 border rounded" value={f.rating} onChange={(e) => setF({ ...f, rating: e.target.value })}>
                                    <option value="">No rating</option>{[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} star{n > 1 ? 's' : ''}</option>)}
                                </select></label>
                            <input type="text" tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} />
                            <fieldset className="border rounded p-3 space-y-2">
                                <legend className="text-sm font-medium px-1">May we publish it?</legend>
                                <label className="flex items-start gap-2 text-sm"><input type="checkbox" checked={f.consent_publish} onChange={(e) => setF({ ...f, consent_publish: e.target.checked })} />
                                    <span>{(info.consent_template || '').replace('{name}', shownName)}</span></label>
                                {f.consent_publish && (
                                    <label className="block text-sm">Show my name as
                                        <select className="w-full p-2 border rounded" value={f.name_style} onChange={(e) => setF({ ...f, name_style: e.target.value })}>
                                            {Object.entries(info.name_styles || {}).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                                        </select></label>
                                )}
                                <p className="text-xs text-slate-500">Leave the box unticked and only your agent and the office will read it. Nothing is published until the managing broker approves it.</p>
                            </fieldset>
                            <button disabled={busy} className="w-full py-2 bg-slate-800 text-white rounded-md font-medium disabled:opacity-50">Send</button>
                        </form>
                    ))}
            </div>
        </div>
    );
};

export default TestimonialFormPage;
